import { NgModule, ErrorHandler } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader} from '@ngx-translate/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBarModule } from '@angular/material';

// Modules
import { AppRoutingModule } from './app-routing.module';
import { MaterialModule } from './shared/modules/material.module';
import { FontAwesomeModule } from './shared/modules/font-awesome.module';
import { SharedModule } from './shared/modules/shared.module';
import { EditModule } from './shared/modules/edit.module';
import { WizardModule } from './features/wizard/wizard.module';
import { AgmCoreModule } from '@agm/core';
import { SharedModule as AccountingSystemModule } from './integrations/accounting_systems/shared/shared.module';
import { SharedModule as FormTemplateSharedModule } from './module/form-templates/shared/shared.module';
import { QuillModule } from 'ngx-quill';

// Components
import { AppComponent } from './app.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { LayoutAccountComponent } from './shared/layouts/layout-account/layout-account.component';
import { LayoutDashboardComponent } from './shared/layouts/layout-dashboard/layout-dashboard.component';
import { ForceupdateComponent } from './shared/components/forceupdate/forceupdate.component';
import { PopupComponent as NotificationPopupComponent } from './shared/components/notifications/popup/popup.component';
import { HeaderComponent as NotificationHeaderComponent } from './shared/components/notifications/header/header.component';
import { ConfirmationComponent } from './shared/components/notifications/confirmation/confirmation.component';
import { AddQuoteComponent } from './module/opportunities/quotes/add-quote/add-quote.component';
import { NameQuoteComponent } from './module/opportunities/quotes/name-quote/name-quote.component';
import { PreviewQuoteComponent } from './module/opportunities/quotes/preview-quote/preview-quote.component';

// Services
import { AuthService } from './auth/auth.service';
import { AccountService } from './services/account.service';
import { CustomTranslateService } from './services/custom-translate.service';
import { LayoutDashboardService } from './shared/layouts/layout-dashboard/layout-dashboard.service';
import { NotificationService } from './services/notification.service';
import { ChecklistsService } from './services/checklist.service';

import { Title } from '@angular/platform-browser';
import { AuthInterceptor } from './interceptors/auth-interceptor';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { AtatusErrorHandler } from './handlers/atatus.handler';
import { ChargebeeModule } from './admin/chargebee/chargebee.module';
import { WebpackTranslateLoader } from './shared/classes/webpack-translate-loader';
import { SearchService } from './services/search.service';
import { AdvanceSearchboxService } from './services/advance-searchbox.service';
import { SharedInputModule } from './shared/modules/shared-input.module';

import { AngularFireMessagingModule } from '@angular/fire/messaging';
import { AngularFireDatabaseModule } from '@angular/fire/database';
import { AngularFireAuthModule } from '@angular/fire/auth';
import { AngularFireModule } from '@angular/fire';
import { MessagingService } from './services/messaging.service';
import { environment } from '../environments/environment';
import { AsyncPipe } from '../../node_modules/@angular/common';

import { PushNotificationsModule } from 'ng-push';
import { AcsSupportService } from './module/acs-support/acs-support.service';
import { DialogComponent } from './account/client-info/dialog/dialog.component';
import { DataSharingService } from './services/data-sharing/data-sharing.service';
import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY, RecaptchaModule, RECAPTCHA_SETTINGS } from 'ng-recaptcha';
import { DashboardModule } from './shared/layouts/layout-dashboard/dashboard/dashboard.module';
import { ProductFoldersModule } from './features/product-folders/product-folders.module';
import { CarouselModule } from 'ngx-bootstrap/carousel';
import { ShContextMenuModule } from 'ng2-right-click-menu';
import { ContextMenuService } from './services/context-menu.service';
import { EventBusService } from './services/event-bus.service';
import { ManualRecaptchaDialogComponent } from './shared/components/recaptcha/manual-recaptcha-dialog.component';
import { RecaptchaFormsModule } from 'ng-recaptcha/forms';
import { SharedUsersModule } from './module/user/shared-users.module';
import { AgGridModule } from 'ag-grid-angular';
import 'ag-grid-enterprise';
import { LicenseManager } from 'ag-grid-enterprise';
import { DataStateStore } from './shared/store/data-state.store';

/// DO NOT TOUCH
LicenseManager.setLicenseKey('Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-104842}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Fieldmagic_Pty_Ltd}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Fieldmagic}_only_for_{5}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Fieldmagic}_need_to_be_licensed___{Fieldmagic}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{9_November_2026}____[v3]_[0102]_MTc5NDE4MjQwMDAwMA==a565b891689bd4687e17ec9a34c5d74f');

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    LayoutAccountComponent,
    LayoutDashboardComponent,
    ForceupdateComponent,
    NotificationPopupComponent,
    NotificationHeaderComponent,
    ConfirmationComponent,
    AddQuoteComponent,
    NameQuoteComponent,
    PreviewQuoteComponent,
    DialogComponent,
    ManualRecaptchaDialogComponent,
  ],
  exports: [
    QuillModule,
  ],
  imports: [
    AgmCoreModule.forRoot({
      apiKey: 'AIzaSyDOMAL3H37-L_Mg5VjLSSFRbGNT5SvSSUk',
      libraries: ['places']
    }),
    BrowserAnimationsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: WebpackTranslateLoader
      }
    }),
    AppRoutingModule,
    MaterialModule,
    FontAwesomeModule,
    SharedModule.forRoot(),
    EditModule,
    WizardModule,
    PopoverModule.forRoot(),
    DragDropModule,
    MatCardModule,
    ChargebeeModule,
    MatSnackBarModule,
    AccountingSystemModule,
    FormTemplateSharedModule,
    QuillModule,
    AngularFireDatabaseModule,
    AngularFireAuthModule,
    AngularFireMessagingModule,
    AngularFireModule.initializeApp(environment.firebase),
    PushNotificationsModule,
    RecaptchaV3Module,
    SharedInputModule,
    DashboardModule,
    ProductFoldersModule,
    CarouselModule.forRoot(),
    ShContextMenuModule,
    RecaptchaFormsModule,
    RecaptchaModule,
    SharedUsersModule,
    AgGridModule.forRoot([
      /// TODO: minimized me with only needed components
    ])
  ],
  entryComponents: [
    ConfirmationComponent,
    ForceupdateComponent,
    AddQuoteComponent,
    NameQuoteComponent,
    PreviewQuoteComponent,
    DialogComponent,
    ManualRecaptchaDialogComponent,
  ],
  providers: [
    SearchService,
    AdvanceSearchboxService,
    AcsSupportService,
    AuthService,
    AccountService,
    ChecklistsService,
    CustomTranslateService,
    LayoutDashboardService,
    NotificationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler,
      useClass: AtatusErrorHandler
    },
    Title,
    MessagingService,
    AsyncPipe,
    DataSharingService,
    ContextMenuService,
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptcha_site_key,
    },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: environment.recaptcha_site_key_manual,
      },
    },
    EventBusService,
    DataStateStore,
  ],
  bootstrap: [ AppComponent ],
})

export class AppModule { }
